import { iconMap, getIconColor } from './icons.js';
import STITCH_TYPES from './stitchTypes.js';

// Helper function to draw SVG path on canvas
const drawSvgPathOnCanvas = (ctx, path, x, y, width, height, viewBox, stroke, strokeWidth, fill) => {
  if (!path) return;
  
  const [vbX, vbY, vbW, vbH] = viewBox.split(' ').map(Number);
  const scaleX = width / vbW;
  const scaleY = height / vbH;
  
  ctx.save();
  ctx.translate(x - width/2, y - height/2);
  ctx.scale(scaleX, scaleY);
  ctx.translate(-vbX, -vbY);
  
  const path2D = new Path2D(path);
  
  if (fill && fill !== 'none') {
    ctx.fillStyle = fill;
    ctx.fill(path2D);
  }
  
  if (stroke && stroke !== 'none') {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth || 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(path2D);
  }
  
  ctx.restore();
};

// Helper function to calculate margins based on label settings
const calculateMargins = (showLabels, margin = 24) => ({
  left: showLabels.left ? margin : 0,
  right: showLabels.right ? margin : 0,
  top: showLabels.top ? margin : 0,
  bottom: showLabels.bottom ? margin : 0
});

// Helper function to calculate cell positions with margin offsets
const getCellPosition = (row, col, cellSize, yscale, margins) => ({
  x: margins.left + col * cellSize,
  y: margins.top + row * cellSize * yscale
});

// Helper function to check if a cell is no-stitch
const isNoStitchCell = (data, row, col, rows, cols) => {
  if (row < 0 || row >= rows || col < 0 || col >= cols) return false;
  return data?.[row]?.[col] === null;
};

// Helper function to wrap text and return lines with total height
export const wrapText = (ctx, text, maxWidth) => {
  const lines = [];
  const paragraphs = text.split('\n');
  
  paragraphs.forEach(paragraph => {
    if (paragraph.trim() === '') {
      lines.push('');
      return;
    }
    
    const words = paragraph.split(' ');
    let currentLine = '';
    
    words.forEach(word => {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine !== '') {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine);
    }
  });
  
  const fontSize = parseInt(ctx.font.match(/\d+/)?.[0] || '12');
  const lineHeight = fontSize * 1.2;
  
  return {
    lines,
    totalHeight: lines.length * lineHeight
  };
};

// Helper function to calculate standardized bounds for any shape type
export const getShapeBounds = (ctx, shape) => {
  switch (shape.type) {
    case 'line': {
      const padding = 5;
      return {
        x: Math.min(shape.startX, shape.endX) - padding,
        y: Math.min(shape.startY, shape.endY) - padding,
        width: Math.abs(shape.endX - shape.startX) + 2 * padding,
        height: Math.abs(shape.endY - shape.startY) + 2 * padding
      };
    }
    case 'rectangle': {
      const padding = 3;
      return {
        x: Math.min(shape.startX, shape.endX) - padding,
        y: Math.min(shape.startY, shape.endY) - padding,
        width: Math.abs(shape.endX - shape.startX) + 2 * padding,
        height: Math.abs(shape.endY - shape.startY) + 2 * padding
      };
    }
    case 'text': {
      ctx.font = `${shape.bold ? 'bold ' : ''}${shape.fontSize || 12}px ${shape.fontFamily || 'Arial'}`;
      const text = shape.text || 'Text';
      const rotation = (shape.rotation || 0) * Math.PI / 180;
      const maxWidth = shape.width || 200;
      
      const { lines, totalHeight } = wrapText(ctx, text, maxWidth);
      const actualWidth = shape.width || Math.min(maxWidth, Math.max(...lines.map(line => ctx.measureText(line).width)));
      const actualHeight = shape.height || totalHeight;
      
      const padding = 3;
      
      if (rotation !== 0) {
        // Calculate the four corners of the unrotated text box
        const corners = [
          { x: 0, y: 0 }, // top-left
          { x: actualWidth, y: 0 }, // top-right
          { x: 0, y: actualHeight }, // bottom-left
          { x: actualWidth, y: actualHeight } // bottom-right
        ];
        
        // Apply rotation to each corner
        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);
        const rotatedCorners = corners.map(corner => ({
          x: shape.startX + (corner.x * cos - corner.y * sin),
          y: shape.startY + (corner.x * sin + corner.y * cos)
        }));
        
        // Find the bounding box that contains all rotated corners
        const minX = Math.min(...rotatedCorners.map(c => c.x));
        const maxX = Math.max(...rotatedCorners.map(c => c.x));
        const minY = Math.min(...rotatedCorners.map(c => c.y));
        const maxY = Math.max(...rotatedCorners.map(c => c.y));
        
        return {
          x: minX - padding,
          y: minY - padding,
          width: (maxX - minX) + 2 * padding,
          height: (maxY - minY) + 2 * padding
        };
      } else {
        // No rotation, use normal bounds
        return {
          x: shape.startX - padding,
          y: shape.startY - padding,
          width: actualWidth + 2 * padding,
          height: actualHeight + 2 * padding
        };
      }
    }
    case 'icon': {
      const size = shape.size || 16;
      const rotation = (shape.rotation || 0) * Math.PI / 180;
      const padding = 3;
      
      if (rotation !== 0) {
        // Calculate the four corners of the unrotated icon box
        const corners = [
          { x: 0, y: 0 }, // top-left
          { x: size, y: 0 }, // top-right
          { x: 0, y: size }, // bottom-left
          { x: size, y: size } // bottom-right
        ];
        
        // Rotate each corner around the center
        const centerX = size / 2;
        const centerY = size / 2;
        const rotatedCorners = corners.map(corner => ({
          x: shape.startX + (corner.x - centerX) * Math.cos(rotation) - (corner.y - centerY) * Math.sin(rotation) + centerX,
          y: shape.startY + (corner.x - centerX) * Math.sin(rotation) + (corner.y - centerY) * Math.cos(rotation) + centerY
        }));
        
        // Find the axis-aligned bounding box of the rotated icon
        const minX = Math.min(...rotatedCorners.map(c => c.x));
        const maxX = Math.max(...rotatedCorners.map(c => c.x));
        const minY = Math.min(...rotatedCorners.map(c => c.y));
        const maxY = Math.max(...rotatedCorners.map(c => c.y));
        
        return {
          x: minX - padding,
          y: minY - padding,
          width: (maxX - minX) + 2 * padding,
          height: (maxY - minY) + 2 * padding
        };
      } else {
        // No rotation, use normal bounds
        return {
          x: shape.startX - padding,
          y: shape.startY - padding,
          width: size + 2 * padding,
          height: size + 2 * padding
        };
      }
    }
    default:
      return { x: 0, y: 0, width: 0, height: 0 };
  }
};

// Helper function to get resize handles for any shape type  
export const getShapeResizeHandles = () => {
  const baseHandles = [
    { type: 'nw', position: 'corner' }, // top-left
    { type: 'ne', position: 'corner' }, // top-right  
    { type: 'sw', position: 'corner' }, // bottom-left
    { type: 'se', position: 'corner' }, // bottom-right
  ];
  
  // All shape types now support mid-point handles for consistent experience
  baseHandles.push(
    { type: 'n', position: 'edge' }, // top-middle
    { type: 's', position: 'edge' }, // bottom-middle
    { type: 'w', position: 'edge' }, // left-middle
    { type: 'e', position: 'edge' } // right-middle
  );
  
  return baseHandles;
};

// Helper function to draw resize handles with consistent styling
export const drawResizeHandles = (ctx, bounds, shape) => {
  const handleSize = 6;
  const handleDefinitions = getShapeResizeHandles(shape);
  
  const handles = handleDefinitions.map(def => {
    let x, y;
    switch (def.type) {
      case 'nw': x = bounds.x; y = bounds.y; break;
      case 'ne': x = bounds.x + bounds.width; y = bounds.y; break;
      case 'sw': x = bounds.x; y = bounds.y + bounds.height; break;
      case 'se': x = bounds.x + bounds.width; y = bounds.y + bounds.height; break;
      case 'n': x = bounds.x + bounds.width / 2; y = bounds.y; break;
      case 's': x = bounds.x + bounds.width / 2; y = bounds.y + bounds.height; break;
      case 'w': x = bounds.x; y = bounds.y + bounds.height / 2; break;
      case 'e': x = bounds.x + bounds.width; y = bounds.y + bounds.height / 2; break;
      default: return null;
    }
    return { x, y, type: def.type };
  }).filter(Boolean);
  
  ctx.setLineDash([]);
  ctx.fillStyle = '#0066ff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  
  handles.forEach(handle => {
    ctx.fillRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
    ctx.strokeRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
  });
};

// Helper function to draw grid lines (horizontal or vertical)
const drawGridLines = (ctx, isHorizontal, count, cellSize, yscale, margins, rows, cols, data, lineWidth = 0.3, intervals = null) => {
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = 'black';
  
  const startValue = intervals ? intervals : 0;
  const endValue = intervals ? count : count + 1;
  const step = intervals || 1;
  
  for (let i = startValue; i < endValue; i += step) {
    if (intervals && i === 0) continue; // Skip first interval line
    
    let currentPos;
    
    if (isHorizontal) {
      const yPos = Math.round(margins.top + i * cellSize * yscale) + 0.5;
      currentPos = margins.left;
      
      // Draw line segments, breaking for no-stitch areas
      for (let x = 0; x <= cols; x++) {
        const cellAbove = i > 0 && x < cols ? isNoStitchCell(data, i - 1, x, rows, cols) : false;
        const cellBelow = i < rows && x < cols ? isNoStitchCell(data, i, x, rows, cols) : false;
        
        const shouldBreak = (cellAbove && cellBelow) || 
                           (cellAbove && i === rows) || 
                           (cellBelow && i === 0);
        
        if (shouldBreak || x === cols) {
          if (currentPos < margins.left + x * cellSize) {
            ctx.beginPath();
            ctx.moveTo(currentPos, yPos);
            ctx.lineTo(margins.left + x * cellSize, yPos);
            ctx.stroke();
          }
          currentPos = margins.left + (x + 1) * cellSize;
        }
      }
    } else {
      const xPos = Math.round(margins.left + i * cellSize) + 0.5;
      currentPos = margins.top;
      
      // Draw line segments, breaking for no-stitch areas
      for (let y = 0; y <= rows; y++) {
        const cellLeft = i > 0 && y < rows ? isNoStitchCell(data, y, i - 1, rows, cols) : false;
        const cellRight = i < cols && y < rows ? isNoStitchCell(data, y, i, rows, cols) : false;
        
        const shouldBreak = (cellLeft && cellRight) || 
                           (cellLeft && i === cols) || 
                           (cellRight && i === 0);
        
        if (shouldBreak || y === rows) {
          if (currentPos < margins.top + y * cellSize * yscale) {
            ctx.beginPath();
            ctx.moveTo(xPos, currentPos);
            ctx.lineTo(xPos, margins.top + y * cellSize * yscale);
            ctx.stroke();
          }
          currentPos = margins.top + (y + 1) * cellSize * yscale;
        }
      }
    }
  }
};

// Helper function to get row/column numbers based on direction and alternating label setting
const getNumberByDirection = (index, total, start, direction, alternatingLabels, position, alternatingStartSide = 'right') => {
  // Calculate the actual number based on direction (handles both vertical and horizontal)
  let actualNumber;
  if (direction === 'up' || direction === 'left') {
    // For 'up' (vertical) or 'left' (horizontal), reverse the numbering
    actualNumber = total - index + start - 1;
  } else {
    // For 'down' (vertical) or 'right' (horizontal), normal numbering
    actualNumber = index + start;
  }
  
  if (alternatingLabels && (position === 'left' || position === 'right')) {
    // For alternating labels (flat knitting), determine if this row should be shown on this side
    const isOddRow = actualNumber % 2 === 1;
    
    // Determine which rows to show based on starting side preference
    if (alternatingStartSide === 'right') {
      // Traditional: Right side shows odd rows (1, 3, 5...), Left shows even rows (2, 4, 6...)
      if (position === 'right') {
        return isOddRow ? actualNumber : null;
      } else {
        return isOddRow ? null : actualNumber;
      }
    } else {
      // Alternative: Left side shows odd rows (1, 3, 5...), Right shows even rows (2, 4, 6...)
      if (position === 'left') {
        return isOddRow ? actualNumber : null;
      } else {
        return isOddRow ? null : actualNumber;
      }
    }
  }
  
  // Default behavior for non-alternating labels or top/bottom labels
  return actualNumber;
};

// Helper function to draw labels for a specific position
const drawLabels = (ctx, position, rows, cols, cellSize, yscale, margins, startRow, startCol, labelDirection, alternatingLabels, margin, alternatingStartSide = 'right') => {
  const labelPadding = 2;
  
  // Calculate font size based on cell size - smaller fonts for small cells
  let fontSize;
  if (cellSize <= 8) {
    fontSize = 5;
  } else if (cellSize <= 10) {
    fontSize = 6;
  } else {
    fontSize = Math.max(8, Math.min(10, cellSize * 0.6));
  }
  
  ctx.font = `${fontSize}px Verdana`;
  ctx.fillStyle = 'black';
  
  switch (position) {
    case 'right':
      // Fill background
      ctx.fillStyle = 'white';
      ctx.fillRect(margins.left + cols * cellSize + labelPadding, margins.top, margin - labelPadding, rows * cellSize * yscale);
      
      ctx.fillStyle = 'black';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      
      for (let row = 0; row < rows; row++) {
        const yPos = margins.top + row * cellSize * yscale;
        const rowNumber = getNumberByDirection(row, rows, startRow, labelDirection.vertical, alternatingLabels, 'right', alternatingStartSide);
        if (rowNumber !== null) {
          ctx.fillText(rowNumber, margins.left + cols * cellSize + labelPadding + 3, yPos + cellSize / 2);
        }
      }
      break;
      
    case 'left':
      // Fill background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, margins.top, margins.left - labelPadding, rows * cellSize * yscale);
      
      ctx.fillStyle = 'black';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      
      for (let row = 0; row < rows; row++) {
        const yPos = margins.top + row * cellSize * yscale;
        const rowNumber = getNumberByDirection(row, rows, startRow, labelDirection.vertical, alternatingLabels, 'left', alternatingStartSide);
        if (rowNumber !== null) {
          ctx.fillText(rowNumber, margins.left - labelPadding - 3, yPos + cellSize / 2);
        }
      }
      break;
      
    case 'bottom':
      // Fill background
      ctx.fillStyle = 'white';
      ctx.fillRect(margins.left, margins.top + rows * cellSize * yscale + labelPadding, cols * cellSize, margin - labelPadding);
      
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      
      for (let col = 0; col < cols; col++) {
        const xPos = margins.left + col * cellSize;
        const colNumber = getNumberByDirection(col, cols, startCol, labelDirection.horizontal, alternatingLabels, 'bottom');
        ctx.fillText(colNumber, xPos + cellSize / 2, margins.top + rows * cellSize * yscale + labelPadding + 3);
      }
      break;
      
    case 'top':
      // Fill background
      ctx.fillStyle = 'white';
      ctx.fillRect(margins.left, 0, cols * cellSize, margins.top - labelPadding);
      
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      
      for (let col = 0; col < cols; col++) {
        const xPos = margins.left + col * cellSize;
        const colNumber = getNumberByDirection(col, cols, startCol, labelDirection.horizontal, alternatingLabels, 'top');
        ctx.fillText(colNumber, xPos + cellSize / 2, margins.top - labelPadding - 3);
      }
      break;
  }
};

// Helper function to draw shapes
const drawShape = (ctx, shape, isPreview = false, isSelected = false) => {
  if (!shape) return;

  ctx.save();

  // Set stroke style
  ctx.strokeStyle = shape.color || '#000000';
  ctx.lineWidth = shape.thickness || 1;
  
  if (isPreview) {
    ctx.globalAlpha = 0.7; // Make preview semi-transparent
  }

  switch (shape.type) {
    case 'line':
      ctx.beginPath();
      ctx.moveTo(shape.startX, shape.startY);
      ctx.lineTo(shape.endX, shape.endY);
      ctx.stroke();
      break;

    case 'rectangle': {
      const rectX = Math.min(shape.startX, shape.endX);
      const rectY = Math.min(shape.startY, shape.endY);
      const rectWidth = Math.abs(shape.endX - shape.startX);
      const rectHeight = Math.abs(shape.endY - shape.startY);
      
      if (shape.filled) {
        ctx.fillStyle = shape.color || '#000000';
        ctx.fillRect(rectX, rectY, rectWidth, rectHeight);
      } else {
        ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
      }
      break;
    }

    case 'text': {
      ctx.fillStyle = shape.color || '#000000';
      ctx.font = `${shape.bold ? 'bold ' : ''}${shape.fontSize || 12}px ${shape.fontFamily || 'Arial'}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      
      // Handle text wrapping and rotation
      const text = shape.text || 'Text';
      const fontSize = shape.fontSize || 12;
      const lineHeight = fontSize * 1.2; // Standard line height
      const rotation = (shape.rotation || 0) * Math.PI / 180; // Convert degrees to radians
      const maxWidth = shape.width || 200; // Use shape width or default
      
      // Use the unified wrapText helper function
      const { lines } = wrapText(ctx, text, maxWidth);
      
      if (rotation !== 0) {
        // Save context and apply rotation
        ctx.save();
        ctx.translate(shape.startX, shape.startY);
        ctx.rotate(rotation);
        
        lines.forEach((line, index) => {
          const yPos = index * lineHeight;
          ctx.fillText(line, 0, yPos);
        });
        
        ctx.restore();
      } else {
        // No rotation, render normally
        lines.forEach((line, index) => {
          const yPos = shape.startY + (index * lineHeight);
          ctx.fillText(line, shape.startX, yPos);
        });
      }
      break;
    }

    case 'icon': {
      const size = shape.size || 16;
      const iconType = shape.iconType || 'arrow';
      const rotation = (shape.rotation || 0) * Math.PI / 180; // Convert degrees to radians
      
      // Get the drawing function for this icon type
      const iconDrawFunction = iconMap[iconType];
      if (iconDrawFunction) {
        ctx.save();
        
        // Translate to the icon position and apply rotation
        const centerX = shape.startX + size / 2;
        const centerY = shape.startY + size / 2;
        ctx.translate(centerX, centerY);
        if (rotation !== 0) {
          ctx.rotate(rotation);
        }
        
        // Draw the icon centered at the origin
        const fillColor = shape.filled ? (shape.color || '#000000') : 'transparent';
        const strokeColor = shape.color || '#000000';
        iconDrawFunction(ctx, 0, 0, size, fillColor, strokeColor);
        
        ctx.restore();
      }
      break;
    }

    default:
      break;
  }

  // Draw selection indicators if the shape is selected
  if (isSelected && !isPreview) {
    ctx.strokeStyle = '#0066ff';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    // Calculate bounding box using unified helper function
    const bounds = getShapeBounds(ctx, shape);
    
    // Draw selection rectangle
    ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
    
    // Draw resize handles using unified helper function
    drawResizeHandles(ctx, bounds, shape);
  }

  ctx.restore();
};

export const drawChart = ({ canvasRef, settings, cols, rows, data, colors, currentRow, selection, isEdit, colorway, knitdata, stitchPreview, colorPreview, shapes, currentShapePreview, selectedShape }) => {
  const margin = settings.margin || 24; // Space for row and column numbers (configurable)
  const shapeMargin = 50; // Extra space around the entire canvas for shapes
  const { 
    cellSize, 
    yscale, 
    thickLineEveryX, 
    thickLineEveryY, 
    startRow = 1, 
    startCol = 1, 
    noStitchColor = '#e0e0e0',
    showLabels = { right: true, left: false, top: false, bottom: true },
    labelDirection = { vertical: 'down', horizontal: 'right' },
    alternatingLabels = false,
    alternatingStartSide = 'right'
  } = settings;
  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  // Calculate margins using helper function
  const margins = calculateMargins(showLabels, margin);
  
  // Add shape margins to the total canvas size
  const totalMargins = {
    left: margins.left + shapeMargin,
    right: margins.right + shapeMargin,
    top: margins.top + shapeMargin,
    bottom: margins.bottom + shapeMargin
  };

  // Get the device pixel ratio
  const dpr = window.devicePixelRatio || 1;

  // Adjust canvas dimensions for high DPI - include shape margins
  canvas.width = (cols * cellSize + totalMargins.left + totalMargins.right) * dpr;
  canvas.height = (rows * cellSize * yscale + totalMargins.top + totalMargins.bottom) * dpr;
  canvas.style.width = `${cols * cellSize + totalMargins.left + totalMargins.right}px`;
  canvas.style.height = `${rows * cellSize * yscale + totalMargins.top + totalMargins.bottom}px`;

  // Scale the context to match the device pixel ratio
  ctx.scale(dpr, dpr);

  // Clear the canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
  // Set white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  
  // Offset the drawing context to account for shape margins
  ctx.save();
  ctx.translate(shapeMargin, shapeMargin);

  // Draw the cells
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      // Get color data (color background)
      const cellValue = data?.[row]?.[col];
      
      // Handle null values (no stitch)
      const isNoStitch = cellValue === null;
      
      const chroma = colorway?.chromas?.[cellValue] || {};
      const fillColor = isNoStitch ? noStitchColor : (chroma.color || (colors && colors[cellValue !== null ? cellValue : 0]) || 'white');
      const strokeColor = getIconColor(fillColor);
      const iconName = chroma.icon; // Get the icon name directly
      const drawIcon = (iconName && iconName !== '') ? iconMap[iconName] : null; // Only get icon function if icon is specified and not empty

      // Calculate positions using helper function
      const { x: xPos, y: yPos } = getCellPosition(row, col, cellSize, yscale, margins);

      // Draw the cell background
      ctx.fillStyle = fillColor;
      ctx.fillRect(xPos, yPos, cellSize, cellSize);

      // Draw the icon in the middle of the cell (if enabled and not no-stitch)
      if (settings.showIcons && drawIcon && !isNoStitch) {
        const centerX = xPos + cellSize / 2;
        const centerY = yPos + (cellSize * yscale) / 2;
        const iconSize = cellSize * 0.4; // Reduced size to make icons smaller

        // Use solid state from colorway if available
        const isSolid = chroma.isSolid || false;
        const iconFillColor = isSolid ? strokeColor : 'transparent';
        
        drawIcon(ctx, centerX, centerY, iconSize, iconFillColor, strokeColor);
      }
    }
  }

  // Draw stitch preview if in stitch mode
  if (stitchPreview) {
    const { row, startCol, endCol, selectedStitch } = stitchPreview;
    
    // Draw highlight overlay on the cells that would be affected
    ctx.fillStyle = 'rgba(100, 150, 255, 0.3)'; // Light blue overlay
    
    for (let col = startCol; col <= endCol; col++) {
      // Calculate positions using helper function
      const { x: xPos, y: yPos } = getCellPosition(row, col, cellSize, yscale, margins);
      ctx.fillRect(xPos, yPos, cellSize, cellSize * yscale);
    }
    
    // Draw border around the entire stitch preview area
    ctx.strokeStyle = 'rgba(100, 150, 255, 0.8)';
    ctx.lineWidth = 0.5;
    
    const { x: previewStartXPos, y: previewStartYPos } = getCellPosition(row, startCol, cellSize, yscale, margins);
    const previewWidth = (endCol - startCol + 1) * cellSize;
    const previewHeight = cellSize * yscale;
    
    ctx.strokeRect(previewStartXPos, previewStartYPos, previewWidth, previewHeight);
    
    // Draw the stitch symbol if available
    if (selectedStitch) {
      const stitch = STITCH_TYPES[selectedStitch];
      
      if (stitch && stitch.symbol && stitch.symbol.path) {
        const symbol = stitch.symbol;
        
        // Calculate center position for the stitch symbol
        const centerX = previewStartXPos + previewWidth / 2;
        const centerY = previewStartYPos + previewHeight / 2;
        
        // Draw the stitch symbol
        drawSvgPathOnCanvas(
          ctx,
          symbol.path,
          centerX,
          centerY,
          previewWidth * 0.8, // Slight padding
          previewHeight * 0.8,
          symbol.viewBox || '0 0 24 24',
          symbol.stroke || '#333',
          symbol.strokeWidth || 1,
          symbol.fill || 'none'
        );
      }
    }
  }

  // Draw color preview if in color mode
  if (colorPreview) {
    const { row, col, currentColor } = colorPreview;
    
    // Get the actual color value
    let previewColor = '#ffffff'; // default fallback
    if (currentColor === 'no-stitch') {
      previewColor = noStitchColor;
    } else if (typeof currentColor === 'number' && colors[currentColor]) {
      previewColor = colors[currentColor];
    }
    
    // Calculate positions using helper function
    const { x: xPos, y: yPos } = getCellPosition(row, col, cellSize, yscale, margins);
    
    // Fill the cell with the selected color at reduced opacity
    ctx.fillStyle = previewColor;
    ctx.globalAlpha = 0.7; // Make it semi-transparent
    ctx.fillRect(xPos, yPos, cellSize, cellSize * yscale);
    ctx.globalAlpha = 1.0; // Reset alpha
    
    // Draw border around the color preview cell
    ctx.strokeStyle = 'rgba(255, 140, 0, 0.9)'; // Orange border to indicate it's a preview
    ctx.lineWidth = 2;
    
    ctx.strokeRect(xPos, yPos, cellSize, cellSize * yscale);
  }

  // Dim all rows except the current row
  if (currentRow >= 0 && !isEdit) {
    ctx.strokeStyle = 'yellow';
    ctx.lineWidth = 3;
    // Calculate Y position using helper function
    const { y: currentRowYPos } = getCellPosition(currentRow, 0, cellSize, yscale, margins);
    ctx.strokeRect(margins.left, currentRowYPos, cols * cellSize, cellSize + 1);
  }

  // Draw grid lines using helper functions (update to 0.3 for thinner lines as needed - TODO - make configurable)
  drawGridLines(ctx, true, rows, cellSize, yscale, margins, rows, cols, data, 0.5); // Horizontal lines
  drawGridLines(ctx, false, cols, cellSize, yscale, margins, rows, cols, data, 0.5); // Vertical lines

  // Draw thick grid lines using helper functions
  if (thickLineEveryY > 0 && thickLineEveryY < rows) {
    drawGridLines(ctx, true, rows, cellSize, yscale, margins, rows, cols, data, 1, thickLineEveryY);
  }
  if (thickLineEveryX > 0 && thickLineEveryX < cols) {
    drawGridLines(ctx, false, cols, cellSize, yscale, margins, rows, cols, data, 1, thickLineEveryX);
  }

  // Draw the selection rectangle
  if (selection) {
    const { startRow, startCol, endRow, endCol } = selection;
    ctx.strokeStyle = 'blue';
    ctx.lineWidth = 2;
    
    // Normalize coordinates to handle selection in any direction
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    
    // Calculate visual positions using helper function
    const { x: selectionX, y: selectionY } = getCellPosition(minRow, minCol, cellSize, yscale, margins);
    
    const selectionWidth = (maxCol - minCol + 1) * cellSize;
    const selectionHeight = (maxRow - minRow + 1) * cellSize * yscale;
    
    ctx.strokeRect(selectionX, selectionY, selectionWidth, selectionHeight);
  }

  // Draw row and column numbers based on label settings
  if (cellSize >= 8) {
    // Draw labels using helper function
    if (showLabels.right) {
      drawLabels(ctx, 'right', rows, cols, cellSize, yscale, margins, startRow, startCol, labelDirection, alternatingLabels, margin, alternatingStartSide);
    }
    if (showLabels.left) {
      drawLabels(ctx, 'left', rows, cols, cellSize, yscale, margins, startRow, startCol, labelDirection, alternatingLabels, margin, alternatingStartSide);
    }
    if (showLabels.bottom) {
      drawLabels(ctx, 'bottom', rows, cols, cellSize, yscale, margins, startRow, startCol, labelDirection, alternatingLabels, margin, alternatingStartSide);
    }
    if (showLabels.top) {
      drawLabels(ctx, 'top', rows, cols, cellSize, yscale, margins, startRow, startCol, labelDirection, alternatingLabels, margin, alternatingStartSide);
    }
  }

  // Draw knit stitches on top of everything else
  if (knitdata) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (knitdata[row] && knitdata[row][col]) {
          const stitchType = knitdata[row][col];
          const stitch = STITCH_TYPES[stitchType];
          
          if (stitch && stitch.symbol && stitch.symbol.path) {
            const symbol = stitch.symbol;
            
            // Calculate positions using helper function
            const stitchWidth = stitch.width || 1;
            const totalWidth = cellSize * stitchWidth;
            
            // Calculate the visual start position of the stitch (left-to-right)
            const visualStartCol = col;
            
            const { x: xPos, y: yPos } = getCellPosition(row, visualStartCol, cellSize, yscale, margins);
            const centerX = xPos + totalWidth / 2;
            const centerY = yPos + (cellSize * yscale) / 2;
            
            // Draw the stitch symbol
            drawSvgPathOnCanvas(
              ctx,
              symbol.path,
              centerX,
              centerY,
              totalWidth * 0.8, // Slight padding
              cellSize * 0.8,
              symbol.viewBox || '0 0 24 24',
              symbol.stroke || '#333',
              symbol.strokeWidth || 1,
              symbol.fill || 'none'
            );
          }
        }
      }
    }
  }

  // Draw shapes on top of everything else
  if (shapes && shapes.length > 0) {
    shapes.forEach((shape) => {
      const isSelected = selectedShape && selectedShape.id === shape.id;
      drawShape(ctx, shape, false, isSelected);
    });
  }

  // Draw current shape preview
  if (currentShapePreview) {
    drawShape(ctx, currentShapePreview, true);
  }
  
  // Draw border around the drawable area to show margin boundaries
  ctx.save();
  
  // Calculate the border area (accounting for the shape margins)
  const borderWidth = 2;
  const totalWidth = cols * cellSize + margins.left + margins.right;
  const totalHeight = rows * cellSize * yscale + margins.top + margins.bottom;
  
  // Draw raised border effect
  ctx.lineWidth = borderWidth;
  
  // Top and left edges (lighter - highlight)
  ctx.strokeStyle = '#f0f0f0';
  ctx.beginPath();
  ctx.moveTo(-shapeMargin, -shapeMargin);
  ctx.lineTo(totalWidth + shapeMargin, -shapeMargin); // Top edge
  ctx.moveTo(-shapeMargin, -shapeMargin);
  ctx.lineTo(-shapeMargin, totalHeight + shapeMargin); // Left edge
  ctx.stroke();
  
  // Bottom and right edges (darker - shadow)
  ctx.strokeStyle = '#c0c0c0';
  ctx.beginPath();
  ctx.moveTo(totalWidth + shapeMargin, -shapeMargin);
  ctx.lineTo(totalWidth + shapeMargin, totalHeight + shapeMargin); // Right edge
  ctx.moveTo(-shapeMargin, totalHeight + shapeMargin);
  ctx.lineTo(totalWidth + shapeMargin, totalHeight + shapeMargin); // Bottom edge
  ctx.stroke();
  
  ctx.restore();
  
  // Restore the context (remove shape margin translation)
  ctx.restore();
};
